import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { getRegistrationStatus } from "./events.api";
import { OrgLogo } from "./OrgLogo";

export function PaymentCallbackPage() {
	const [searchParams] = useSearchParams();
	const transactionUuid = searchParams.get("transaction_uuid");

	const [state, setState] = React.useState<"checking" | "success" | "failed">("checking");
	const [message, setMessage] = React.useState("Confirming your payment...");

	React.useEffect(() => {
		if (!transactionUuid) {
			setState("failed");
			setMessage("No transaction reference was provided.");
			return;
		}

		let attempts = 0;
		const maxAttempts = 8;
		let cancelled = false;

		const check = async () => {
			attempts += 1;

			try {
				const response = await getRegistrationStatus(transactionUuid);
				if (cancelled) return;

				const status = response.data.status;

				if (status === "completed") {
					setState("success");
					setMessage("Payment confirmed. Your registration is complete.");
					return;
				}

				if (status === "failed" || status === "cancelled") {
					setState("failed");
					setMessage("Payment was not completed. Please try registering again.");
					return;
				}

				if (attempts >= maxAttempts) {
					setState("failed");
					setMessage("We haven't received confirmation yet. If you completed payment, it may take a few more minutes to reflect.");
					return;
				}

				setTimeout(check, 3000);
			} catch {
				if (!cancelled && attempts < maxAttempts) {
					setTimeout(check, 3000);
				}
			}
		};

		check();

		return () => {
			cancelled = true;
		};
	}, [transactionUuid]);

	return (
		<div className="min-h-screen bg-background py-15 flex flex-col items-center justify-center px-4 gap-6">
			<OrgLogo />
			<Card className="max-w-md w-full text-center">
				<CardContent className="pt-8 pb-8 space-y-4">
					{state === "checking" && <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />}
					{state === "success" && <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />}
					{state === "failed" && <XCircle className="mx-auto h-12 w-12 text-red-600" />}
					<h2 className="text-lg font-semibold">
						{state === "checking" ? "Confirming Payment" : state === "success" ? "Registration Complete" : "Payment Not Completed"}
					</h2>
					<p className="text-sm text-muted-foreground">{message}</p>
				</CardContent>
			</Card>
		</div>
	);
}
